import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Check, ArrowRight, Server, Cpu, Globe2 } from "lucide-react";
import ActivateButton from "@/components/ActivateButton";

function PlanCard({ svc, plan, highlight }) {
  const daily = plan.price_usd / 30;
  return (
    <div
      data-testid={`pricing-plan-${svc.slug}-${plan.code}`}
      className={`card-flat p-6 flex flex-col ${highlight ? "border-signal" : ""}`}
    >
      <div className="flex items-center justify-between">
        <span className="badge-mono">{svc.subtype === "dedicated" ? "DEDICATED" : svc.subtype === "rest_api" ? "API" : "VPS"}</span>
        {highlight && <span className="badge-mono border-signal/60 text-signal">POPULAR</span>}
      </div>
      <div className="mt-4">
        <div className="font-display text-xl tracking-tight">{svc.name}</div>
        <div className="font-mono text-[11px] text-zinc-500 mt-1">{plan.name}</div>
      </div>
      <div className="mt-5 flex items-baseline gap-1 font-mono">
        <span className="text-zinc-500 text-sm">$</span>
        <span className="text-4xl text-white font-semibold leading-none">{plan.price_usd.toFixed(0)}</span>
        <span className="text-zinc-600 text-xs ml-1 uppercase tracking-widest">
          / {plan.period === "month" ? "MO" : plan.period}
        </span>
      </div>
      <div className="mt-1 font-mono text-[11px] text-zinc-500">
        ≈ ${daily.toFixed(2)}/day from your balance
      </div>
      {plan.quota && (
        <div className="mt-2 font-mono text-[11px] text-zinc-500">{plan.quota}</div>
      )}
      <ul className="mt-5 space-y-2 flex-1">
        {svc.features.slice(0, 4).map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm text-zinc-300">
            <Check size={14} className="mt-1 text-signal shrink-0" />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <div className="mt-6 flex flex-col gap-2">
        <ActivateButton
          serviceSlug={svc.slug}
          planCode={plan.code}
          dailyCost={daily}
          monthlyCost={plan.price_usd}
          fullWidth
          className={
            highlight
              ? "btn-signal h-10 px-4 text-xs font-mono tracking-widest"
              : "inline-flex items-center justify-center gap-2 h-10 px-4 rounded-sm border border-zinc-800 text-zinc-200 hover:border-zinc-500 font-mono text-xs tracking-widest transition-colors"
          }
          testId={`buy-${svc.slug}-${plan.code}`}
        >
          ACTIVATE
        </ActivateButton>
        <Link
          to={`/services/${svc.slug}`}
          className="text-center font-mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white"
        >
          See details
        </Link>
      </div>
    </div>
  );
}

export default function Pricing() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).finally(() => setLoading(false));
  }, []);

  const vps = services.filter((s) => s.subtype === "vps");
  const dedicated = services.filter((s) => s.subtype === "dedicated");
  const apis = services.filter((s) => s.category === "api_services");

  const pickPlan = (svc) => {
    if (!svc?.plans || svc.plans.length === 0) return null;
    const idx = Math.min(1, svc.plans.length - 1);
    return svc.plans[idx];
  };

  return (
    <div data-testid="pricing-page" className="container-pad py-12 md:py-20">
      <div className="overline">// PRICING · TRANSPARENT · USD</div>
      <h1 className="mt-4 font-display text-4xl md:text-6xl tracking-tight max-w-4xl">
        Pay only for the server you run. <span className="text-signal">No hidden fees.</span>
      </h1>
      <p className="mt-4 max-w-2xl text-zinc-400 text-base md:text-lg">
        Top up your CloudRent balance once, then activate any plan — daily fees are deducted
        automatically. Plans are quoted in USD. Cancel anytime from your dashboard.
      </p>

      <div className="mt-10 flex flex-wrap gap-3">
        <a href="#vps" className="btn-ghost h-10 px-4 text-xs font-mono tracking-widest">
          <Server size={14} /> VPS PLANS
        </a>
        <a href="#dedicated" className="btn-ghost h-10 px-4 text-xs font-mono tracking-widest">
          <Cpu size={14} /> DEDICATED PLANS
        </a>
        <a href="#apis" className="btn-ghost h-10 px-4 text-xs font-mono tracking-widest">
          <Globe2 size={14} /> API PLANS
        </a>
      </div>

      {/* VPS */}
      <section id="vps" className="mt-16 scroll-mt-20">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="overline">// CATEGORY 01</div>
            <h2 className="mt-2 font-display text-3xl tracking-tight">VPS pricing</h2>
          </div>
          <Link to="/catalog?subtype=vps" className="hidden md:inline-flex items-center gap-2 font-mono text-xs tracking-widest text-zinc-400 hover:text-white">
            COMPARE ALL <ArrowRight size={14} />
          </Link>
        </div>
        {loading ? (
          <div className="font-mono text-xs text-zinc-500">LOADING…</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {vps.map((s, i) => {
              const plan = pickPlan(s);
              if (!plan) return null;
              return <PlanCard key={s.id} svc={s} plan={plan} highlight={i === 0} />;
            })}
          </div>
        )}
      </section>

      {/* DEDICATED */}
      <section id="dedicated" className="mt-20 scroll-mt-20">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="overline">// CATEGORY 02</div>
            <h2 className="mt-2 font-display text-3xl tracking-tight">Dedicated pricing</h2>
          </div>
          <Link to="/catalog?subtype=dedicated" className="hidden md:inline-flex items-center gap-2 font-mono text-xs tracking-widest text-zinc-400 hover:text-white">
            COMPARE ALL <ArrowRight size={14} />
          </Link>
        </div>
        {loading ? (
          <div className="font-mono text-xs text-zinc-500">LOADING…</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {dedicated.map((s, i) => {
              const plan = pickPlan(s);
              if (!plan) return null;
              return <PlanCard key={s.id} svc={s} plan={plan} highlight={i === 0} />;
            })}
          </div>
        )}
      </section>

      {/* APIS */}
      <section id="apis" className="mt-20 scroll-mt-20">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="overline">// CATEGORY 03</div>
            <h2 className="mt-2 font-display text-3xl tracking-tight">Developer API pricing</h2>
          </div>
          <Link to="/catalog?category=api_services" className="hidden md:inline-flex items-center gap-2 font-mono text-xs tracking-widest text-zinc-400 hover:text-white">
            COMPARE ALL <ArrowRight size={14} />
          </Link>
        </div>
        {loading ? (
          <div className="font-mono text-xs text-zinc-500">LOADING…</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {apis.map((s, i) => {
              const plan = pickPlan(s);
              if (!plan) return null;
              return <PlanCard key={s.id} svc={s} plan={plan} highlight={i === 0} />;
            })}
          </div>
        )}
      </section>

      {/* FAQ */}
      <section className="mt-24 grid grid-cols-1 md:grid-cols-12 gap-8">
        <div className="md:col-span-4">
          <div className="overline">// FAQ</div>
          <h2 className="mt-2 font-display text-3xl tracking-tight">
            Pricing <span className="text-signal">FAQ</span>
          </h2>
        </div>
        <div className="md:col-span-8 divide-y divide-zinc-800 border-y border-zinc-800">
          {[
            { q: "Which currencies are supported?", a: "All prices are quoted and charged in USD. Conversion is performed by your card issuer at the prevailing rate." },
            { q: "Can I cancel anytime?", a: "Yes. Cancel from your dashboard and your service stays active until the end of the billing period." },
            { q: "Do you offer refunds?", a: "Yes — see our refund policy. We refund within 14 days of purchase for unused services." },
            { q: "Are there volume discounts?", a: "Yes, above 100 server instances. Contact sales@cloudrentvps.com." },
            { q: "How is payment processed?", a: "Checkout is handled by a certified third-party provider. Card details are entered on their hosted environment — never on our servers. You'll receive a receipt by email." },
          ].map((f, i) => (
            <details key={i} className="group py-5">
              <summary className="cursor-pointer flex items-center justify-between font-display text-lg tracking-tight">
                {f.q}
                <span className="text-signal group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="mt-3 text-zinc-400 text-sm leading-relaxed max-w-2xl">{f.a}</p>
            </details>
          ))}
        </div>
      </section>
    </div>
  );
}
